import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  NotificationsOff as NotificationsOffIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import PageLayout from '../../components/layout/PageLayout';
import PageHeader from '../../components/layout/PageHeader';
import { Loading, StackSkeleton, TaskRowSkeleton } from '../../components/layout/Skeletons';
import { useTasks } from '../../hooks/account/useTasks';
import { toCategoryOptions } from '../../hooks/directory/useDirectory';
import { apiCall } from '../../utils/fetchAPI';
import { useToast } from '../../context/ToastContext';
import { TASK_RECURRENCES, formatDate } from '../../constants/scheduling';

const EMPTY = {
  title: '',
  dueOn: '',
  recurrence: 'ONCE',
  notes: '',
  categorySlug: '',
  remindByEmail: true,
};

/**
 * The form for one chore. Also used to edit, because adding and changing are
 * the same fields.
 */
function TaskDialog({ open, initial, categories, onClose, onSave }) {
  const [form, setForm] = useState(initial ?? EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Re-seed when the dialog is opened on a different task.
  const [seed, setSeed] = useState(initial);

  if (seed !== initial) {
    setSeed(initial);
    setForm(initial ?? EMPTY);
    setError('');
  }

  const update = (field) => (event) => setForm({ ...form, [field]: event.target.value });

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');

    const message = await onSave({
      title: form.title.trim(),
      dueOn: form.dueOn,
      recurrence: form.recurrence,
      notes: form.notes.trim() || null,
      categorySlug: form.categorySlug || null,
      remindByEmail: form.remindByEmail,
    });

    setSaving(false);

    if (message) {
      setError(message);
      return;
    }

    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <form onSubmit={submit}>
        <DialogTitle>{initial?.uuid ? 'Modifier la tâche' : 'Ajouter une tâche'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Quoi"
              value={form.title}
              onChange={update('title')}
              required
              disabled={saving}
              placeholder="Entretien de la chaudière"
              inputProps={{ maxLength: 180 }}
            />

            <TextField
              label="Échéance"
              type="date"
              value={form.dueOn}
              onChange={update('dueOn')}
              required
              disabled={saving}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              select
              label="Périodicité"
              value={form.recurrence}
              onChange={update('recurrence')}
              disabled={saving}
              helperText="Une tâche qui revient se reprogramme toute seule une fois cochée."
            >
              {TASK_RECURRENCES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            {categories.length > 0 && (
              <TextField
                select
                label="Métier concerné (facultatif)"
                value={form.categorySlug}
                onChange={update('categorySlug')}
                disabled={saving}
                helperText="Pour retrouver un professionnel en un clic le moment venu."
              >
                <MenuItem value="">Aucun</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category.slug} value={category.slug}>
                    {category.label}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <TextField
              label="Notes"
              value={form.notes}
              onChange={update('notes')}
              multiline
              minRows={3}
              disabled={saving}
              inputProps={{ maxLength: 2000 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={form.remindByEmail}
                  onChange={(event) => setForm({ ...form, remindByEmail: event.target.checked })}
                  disabled={saving}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">Me le rappeler par courriel</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Un courriel part une semaine avant l’échéance.
                  </Typography>
                </Box>
              }
            />

            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving} variant="outlined">
            Annuler
          </Button>
          <Button type="submit" variant="contained" disabled={saving}>
            Enregistrer
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

/**
 * Home upkeep: the half of the product that belongs to the resident alone.
 *
 * Nobody else sees this list, and it works whether or not a professional is
 * ever booked from it.
 */
function MyTasks() {
  const toast = useToast();
  // The category list is only there to tag a chore with a trade, so it fails
  // quietly: a task with no category is perfectly usable.
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    let cancelled = false;

    apiCall('/categories', { method: 'GET' })
      .then((response) => (response.ok ? response.json() : { data: [] }))
      .then((data) => {
        if (!cancelled) {
          setCategories(toCategoryOptions(data.data || []));
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  const { tasks, loading, error, includeDone, setIncludeDone, save, setDone, remove } = useTasks();
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const openNew = () => {
    setEditing(null);
    setOpen(true);
  };

  const openEdit = (task) => {
    setEditing({
      uuid: task.uuid,
      title: task.title,
      dueOn: task.due_on,
      recurrence: task.recurrence,
      notes: task.notes || '',
      categorySlug: task.category?.slug || '',
      remindByEmail: task.remind_by_email,
    });
    setOpen(true);
  };

  const handleSave = async (payload) => {
    const message = await save(payload, editing?.uuid ?? null);

    if (!message) {
      toast.success(editing?.uuid ? 'Tâche mise à jour.' : 'Tâche ajoutée.');
    }

    return message;
  };

  const toggle = async (task) => {
    const message = await setDone(task.uuid, !task.done);

    if (message) {
      toast.error(message);
      return;
    }

    // A recurring task does not disappear, so say what actually happened.
    toast.success(
      task.done
        ? 'Tâche remise sur la liste.'
        : 'ONCE' === task.recurrence
          ? 'Tâche terminée.'
          : 'Fait. Prochaine échéance programmée.'
    );
  };

  const drop = async (task) => {
    const message = await remove(task.uuid);

    if (message) {
      toast.error(message);
      return;
    }

    toast.success('Tâche supprimée.');
  };

  return (
    <PageLayout centered={false}>
      <Box sx={{ width: '100%' }}>
        <PageHeader
          title="Mes travaux"
          lede="L’entretien à ne pas oublier. Vous êtes seul à voir cette liste, et un rappel part par courriel à l’approche de chaque échéance — vous pouvez le couper tâche par tâche."
          action={
            <Button variant="contained" size="large" startIcon={<AddIcon />} onClick={openNew}>
              Ajouter une tâche
            </Button>
          }
        />

        <FormControlLabel
          control={<Switch checked={includeDone} onChange={(e) => setIncludeDone(e.target.checked)} />}
          label={<Typography variant="body1">Voir aussi ce qui est fait</Typography>}
          sx={{ mb: 2.5 }}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Loading label="Chargement de vos travaux">
            <StackSkeleton count={4} gap={1.75}>{TaskRowSkeleton}</StackSkeleton>
          </Loading>
        ) : (
          <Stack spacing={1.75}>
            {0 === tasks.length && (
              <Paper variant="outlined" sx={{ p: 5, textAlign: 'center', borderStyle: 'dashed' }}>
                <Typography variant="h4" component="p" sx={{ mb: 1 }}>
                  Rien à faire pour l’instant.
                </Typography>
                <Typography variant="body1">
                  Chaudière, ramonage, VMC, détecteurs de fumée : notez-les une fois et ils
                  reviendront tout seuls.
                </Typography>
              </Paper>
            )}

            {tasks.map((task) => (
              <Paper
                key={task.uuid}
                variant="outlined"
                sx={{
                  p: { xs: 2, md: 2.5 },
                  borderColor: task.overdue ? 'error.main' : 'divider',
                  borderWidth: task.overdue ? 2 : 1,
                  bgcolor: task.done ? 'background.default' : 'background.paper',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                  <Checkbox
                    checked={task.done}
                    onChange={() => toggle(task)}
                    sx={{ mt: -0.5 }}
                    // Without this the box announces itself as an unnamed
                    // checkbox, which tells a screen-reader user nothing about
                    // what they are about to tick off.
                    inputProps={{ 'aria-label': `Marquer « ${task.title} » comme fait` }}
                  />

                  <Box sx={{ flexGrow: 1 }}>
                    <Typography
                      variant="h4"
                      component="h2"
                      sx={{
                        textDecoration: task.done ? 'line-through' : 'none',
                        color: task.done ? 'text.disabled' : 'text.primary',
                      }}
                    >
                      {task.title}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.5 }}>
                      <Chip
                        size="small"
                        label={task.overdue ? `En retard — ${formatDate(task.due_on)}` : formatDate(task.due_on)}
                        color={task.overdue ? 'error' : task.due_soon ? 'warning' : 'default'}
                      />
                      {'ONCE' !== task.recurrence && (
                        <Chip size="small" variant="outlined" label={task.recurrence_label} />
                      )}
                      {!task.remind_by_email && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label="Sans rappel"
                          icon={<NotificationsOffIcon />}
                        />
                      )}
                      {task.category && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={task.category.name}
                          component={RouterLink}
                          to={`/services?category=${task.category.slug}`}
                          clickable
                          icon={<SearchIcon />}
                        />
                      )}
                    </Box>

                    {task.notes && (
                      <Typography variant="body2" sx={{ mt: 1.25, whiteSpace: 'pre-line' }}>
                        {task.notes}
                      </Typography>
                    )}

                    {task.last_completed_on && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                        Dernière fois le {formatDate(task.last_completed_on)}
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', gap: 1.25, mt: 1.75, flexWrap: 'wrap' }}>
                      <Button size="small" variant="outlined" onClick={() => openEdit(task)}>
                        Modifier
                      </Button>
                      <Button size="small" variant="outlined" color="error" onClick={() => drop(task)}>
                        Supprimer
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Stack>
        )}
      </Box>

      <TaskDialog
        open={open}
        initial={editing}
        categories={categories}
        onClose={() => setOpen(false)}
        onSave={handleSave}
      />
    </PageLayout>
  );
}

export default MyTasks;
